import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import MarketingShell from "./MarketingShell";
import { StatusChip } from "./MarketingOverview";
import { Plus, Copy, Trash, Eye, PencilSimple, Pause, Play } from "@phosphor-icons/react";
import { formatLocal, formatLocalDate } from "@/lib/timezone";

export default function CampaignsListPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketing/campaigns");
      setRows(r.data);
    } catch {
      toast.error("Failed to load campaigns");
    }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const filtered = rows.filter(r =>
    (filter === "all" || r.status === filter) &&
    (!q || (r.name || "").toLowerCase().includes(q.toLowerCase())
      || (r.subject || "").toLowerCase().includes(q.toLowerCase()))
  );

  const clone = async (id) => {
    try {
      const r = await api.post(`/marketing/campaigns/${id}/clone`);
      toast.success("Campaign cloned");
      nav(`/marketing/campaigns/${r.data.id}/edit`);
    } catch { toast.error("Clone failed"); }
  };

  const pauseOrResume = async (c) => {
    try {
      if (c.status === "paused") {
        await api.post(`/marketing/campaigns/${c.id}/resume`);
        toast.success("Resumed");
      } else if (c.status === "sending" || c.status === "scheduled") {
        await api.post(`/marketing/campaigns/${c.id}/pause`);
        toast.success("Paused");
      } else {
        toast.info(`Cannot pause a ${c.status} campaign`);
        return;
      }
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this campaign? Sent campaigns will be archived instead.")) return;
    try {
      await api.delete(`/marketing/campaigns/${id}`);
      toast.success("Deleted");
      reload();
    } catch { toast.error("Delete failed"); }
  };

  const fmt = (s) => s ? formatLocalDate(s) : "—";

  return (
    <MarketingShell>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search campaigns…"
            className="h-9 px-3 text-sm border border-[#E5E5E5] bg-white w-64 focus:outline-none focus:border-[#0055FF]"
            data-testid="campaign-search"
          />
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="h-9 px-2 text-xs border border-[#E5E5E5] bg-white focus:outline-none"
            data-testid="campaign-filter"
          >
            {["all", "draft", "scheduled", "sending", "sent", "paused", "archived"].map(s =>
              <option key={s} value={s}>{s === "all" ? "All statuses" : s.charAt(0).toUpperCase() + s.slice(1)}</option>
            )}
          </select>
        </div>
        <button onClick={() => nav("/marketing/campaigns/new")} className="bg-[#0055FF] hover:bg-[#0040CC] text-white text-xs font-bold uppercase tracking-wider px-4 h-9 flex items-center gap-1.5" data-testid="new-campaign-btn">
          <Plus size={14} weight="bold" />
          New Campaign
        </button>
      </div>

      <div className="bg-white border border-[#E5E5E5]">
        {loading ? (
          <p className="p-8 text-center text-sm text-[#A3A3A3]">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="p-10 text-center text-sm text-[#A3A3A3]">No campaigns match. <button onClick={() => nav("/marketing/campaigns/new")} className="text-[#0055FF] font-bold underline">Create one?</button></p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-5 py-2 font-bold">Name</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="text-left px-3 py-2 font-bold">Subject</th>
                <th className="text-right px-3 py-2 font-bold">Recipients</th>
                <th className="text-right px-3 py-2 font-bold">Created</th>
                <th className="text-right px-3 py-2 font-bold">Updated</th>
                <th className="text-right px-3 py-2 font-bold">Last run</th>
                <th className="text-right px-5 py-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const canPauseResume = ["sending", "scheduled", "paused"].includes(c.status);
                // Sent campaigns are editable — see CampaignEditorPage
                // for why. They used to show a read-only eye here, so
                // there was no way into the content of a campaign that
                // had gone out.
                const isEditable = !["sending", "archived"].includes(c.status);
                const open = () => nav(`/marketing/campaigns/${c.id}`);
                return (
                <tr key={c.id} className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA]" data-testid={`campaign-row-${c.id}`}>
                  <td className="px-5 py-3 font-semibold text-[#0A0A0A] cursor-pointer" onClick={open}>{c.name}</td>
                  <td className="px-3 py-3 cursor-pointer" onClick={open}><StatusChip status={c.status} /></td>
                  <td className="px-3 py-3 text-[#525252] max-w-[260px] truncate cursor-pointer" onClick={open}>{c.subject || <span className="text-[#A3A3A3] italic">No subject</span>}</td>
                  <td className="px-3 py-3 text-right tabular-nums cursor-pointer" onClick={open}>{c.recipient_count ?? "—"}</td>
                  <td className="px-3 py-3 text-right text-xs text-[#A3A3A3] tabular-nums" title={c.created_at || ""}>{fmt(c.created_at)}</td>
                  <td className="px-3 py-3 text-right text-xs text-[#A3A3A3] tabular-nums" title={c.updated_at || ""}>{fmt(c.updated_at)}</td>
                  <td className="px-3 py-3 text-right text-xs text-[#A3A3A3] tabular-nums" title={c.sent_at || c.scheduled_at || ""}>
                    {c.sent_at ? fmt(c.sent_at) : c.scheduled_at ? <span className="text-[#D97706]">{fmt(c.scheduled_at)}</span> : "—"}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button onClick={(e) => { e.stopPropagation(); nav(`/marketing/campaigns/${c.id}/edit`); }}
                            title={isEditable ? "Save / edit" : "View content"}
                            className="p-1 text-[#525252] hover:text-[#0055FF]"
                            data-testid={isEditable ? `edit-${c.id}` : `view-${c.id}`}>
                      {isEditable ? <PencilSimple size={14} /> : <Eye size={14} />}
                    </button>
                    {canPauseResume && (
                      <button onClick={(e) => { e.stopPropagation(); pauseOrResume(c); }}
                              title={c.status === "paused" ? "Resume" : "Pause"}
                              className={`p-1 ${c.status === "paused" ? "text-[#16A34A] hover:text-[#059669]" : "text-[#D97706] hover:text-[#B45309]"}`}
                              data-testid={`${c.status === "paused" ? "resume" : "pause"}-${c.id}`}>
                        {c.status === "paused" ? <Play size={14} /> : <Pause size={14} />}
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); clone(c.id); }} title="Clone" className="p-1 text-[#525252] hover:text-[#0055FF]" data-testid={`clone-${c.id}`}>
                      <Copy size={14} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); remove(c.id); }} title="Delete" className="p-1 text-[#525252] hover:text-[#DC2626]" data-testid={`delete-${c.id}`}>
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>
    </MarketingShell>
  );
}
